import type { IEmployeeViewModel, RequestDTO } from '@/types/employee';
import type { OrgDepartment, OrgEmployee, OrgPosition } from '@/types/organizationIndexedDB';
import { getOrganizationIndexedDB } from '@/utils/organizationIndexedDBUtils';

export type EmployeePagerSdkResponse = {
  success: true;
  data: IEmployeeViewModel[];
};

const DEFAULT_PAGE_SIZE = 20;

function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'd')
    .toLowerCase()
    .trim();
}

function employeeIdsEqual(left: string, right: string): boolean {
  const a = String(left || '').trim();
  const b = String(right || '').trim();
  if (!a || !b) return false;
  if (a === b) return true;
  const strip = (id: string) => id.replace(/_nv$/i, '').replace(/^nv_/i, '');
  return strip(a) === strip(b);
}

function parseIdList(raw?: string | null): string[] {
  if (!raw) return [];
  return String(raw)
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

function isPrimaryEmployee(emp: OrgEmployee): boolean {
  const parent = String(emp.Parent ?? '').trim();
  return !parent || parent === '0';
}

function matchesPhanHe(emp: OrgEmployee, phanHe?: string | null): boolean {
  const wanted = String(phanHe || '').trim().toLowerCase();
  if (!wanted) return true;
  if (emp.PhanHe == null || emp.PhanHe === '') return true;
  const empPhanHe = String(emp.PhanHe).toLowerCase();
  if (/^\d+$/.test(empPhanHe)) return true;
  return empPhanHe.includes(wanted);
}

function matchesQuery(emp: OrgEmployee, query?: string | null): boolean {
  const needle = normalizeSearchText(String(query || ''));
  if (!needle) return true;
  return (
    normalizeSearchText(emp.TenNhanVien || '').includes(needle) ||
    normalizeSearchText(emp.MaNhanVien || '').includes(needle)
  );
}

function toViewModel(
  emp: OrgEmployee,
  deptById: Map<string, OrgDepartment>,
  posById: Map<string, OrgPosition>,
): IEmployeeViewModel {
  const dept = deptById.get(String(emp.BPID || ''));
  const pos = posById.get(String(emp.CVID || ''));
  return {
    id: emp.ID,
    ID: emp.ID,
    maNhanVien: emp.MaNhanVien ?? '',
    tenNhanVien: emp.TenNhanVien,
    TenNhanVien: emp.TenNhanVien,
    hinh: emp.Hinh,
    bpid: emp.BPID,
    BPID: emp.BPID,
    cvid: emp.CVID,
    tenBoPhan: dept?.Ten ?? '',
    tenChucVu: pos?.Ten ?? '',
    nghiViec: emp.NghiViec,
    parent: emp.Parent,
    phanHe: emp.PhanHe as IEmployeeViewModel['phanHe'],
    site: '',
  };
}

function findEmployeeById(employees: OrgEmployee[], id: string): OrgEmployee | undefined {
  return employees.find((emp) => employeeIdsEqual(emp.ID, id));
}

/**
 * Thay pager-sdk: lọc / phân trang nhân viên từ IndexedDB, giữ contract `{ success, data }`.
 */
export async function pagerEmployeesFromIndexedDB(
  requestDTO: RequestDTO,
): Promise<EmployeePagerSdkResponse> {
  const org = await getOrganizationIndexedDB();
  const employees = org?.employees ?? [];
  const departments = org?.departments ?? [];
  const positions = org?.positions ?? [];

  const deptById = new Map(departments.map((dept) => [String(dept.ID), dept]));
  const posById = new Map(positions.map((pos) => [String(pos.ID), pos]));

  const pageNumber = Math.max(1, Number(requestDTO.pageNumber) || 1);
  const pageSize = Math.max(1, Number(requestDTO.pageSize) || DEFAULT_PAGE_SIZE);
  const ignoreIds = parseIdList(requestDTO.ignoreID);
  const searchIds = parseIdList(requestDTO.employeeSearchIDs);
  const departmentID = String(requestDTO.departmentID || '').trim();
  const positionID = String(requestDTO.positionID || '').trim();
  const workingOnly =
    !requestDTO.trangThaiLamViec || requestDTO.trangThaiLamViec === 'Đang làm việc';
  const includeKiemNhiem = Boolean(requestDTO.layKiemNghiem);

  const filtered = employees.filter((emp) => {
    const id = String(emp.ID || '').trim();
    if (!id) return false;
    if (ignoreIds.some((ignoreId) => employeeIdsEqual(id, ignoreId))) return false;
    if (workingOnly && emp.NghiViec === true) return false;
    if (!includeKiemNhiem && !isPrimaryEmployee(emp)) return false;
    if (!matchesPhanHe(emp, requestDTO.phanHe)) return false;
    if (departmentID && String(emp.BPID || '') !== departmentID) return false;
    if (positionID && String(emp.CVID || '') !== positionID) return false;
    if (!matchesQuery(emp, requestDTO.query)) return false;
    return true;
  });

  let ordered = filtered;
  if (searchIds.length > 0) {
    const selected: OrgEmployee[] = [];
    const rest: OrgEmployee[] = [];
    const selectedFound = new Set<string>();

    for (const emp of filtered) {
      if (searchIds.some((searchId) => employeeIdsEqual(emp.ID, searchId))) {
        selected.push(emp);
        selectedFound.add(emp.ID);
      } else {
        rest.push(emp);
      }
    }

    for (const searchId of searchIds) {
      if ([...selectedFound].some((id) => employeeIdsEqual(id, searchId))) continue;
      const extra = findEmployeeById(employees, searchId);
      if (extra) {
        selected.push(extra);
        selectedFound.add(extra.ID);
      }
    }

    selected.sort((a, b) => {
      const indexA = searchIds.findIndex((searchId) => employeeIdsEqual(a.ID, searchId));
      const indexB = searchIds.findIndex((searchId) => employeeIdsEqual(b.ID, searchId));
      return indexA - indexB;
    });
    ordered = [...selected, ...rest];
  }

  const start = (pageNumber - 1) * pageSize;
  const page = ordered
    .slice(start, start + pageSize)
    .map((emp) => toViewModel(emp, deptById, posById));

  return { success: true, data: page };
}
